import { useEffect, useRef, useState } from 'preact/hooks';
import micIcon from '/src/assets/micIcon.svg';
import iconForDeleteAudio from '/src/assets/iconForDeleteAudio.svg';
import {
  CHATBOT_CHAT_MSJ_FROM_BOT,
  CHATBOT_CHAT_MSJ_FROM_CLIENT,
  CHATBOT_ERROR_ACCESSING_MEDIA_DEVICES,
  CHATBOT_ERROR_MESSAGE_AUDIO
} from '../utils/constants.js';
import recIcon from '/src/assets/recIcon.svg';

import SendButtonImgChatbot from './sendButtonImgChatbot.jsx';
import clsx from 'clsx';
const chatbotUtnMineTypeAudio = 'video/mp4; codecs="avc1.4d002a"';

// We'll track audio activity during recording instead of checking after
// Using a very low threshold to detect even quiet sounds
let hasAudioActivity = false;
let audioActivityThreshold = 2; // Very low threshold to detect even quiet audio

export default function AudioRecorderChatbot({ isLoading, sendMessage, textareaRef, setIsRecording, isRecording }) {
  const [recordOptionsOpened, setRecordOptionsOpened] = useState(false);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const selectRef = useRef(null);
  const timerRef = useRef(null);
  const mouseDownExecutedRef = useRef(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [audioData, setAudioData] = useState(null);

  const isCancelRef = useRef(false);

  const [volumeBars, setVolumeBars] = useState([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const scriptProcessorRef = useRef(null);
  const volumeIntervalRef = useRef(null);
  const recordingTimeoutRef = useRef(null);

  async function getDevices() {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      setDevices(audioInputs);
      if (audioInputs.length > 0) {
        setSelectedDevice(audioInputs[0].deviceId);
      }
    } catch (error) {
      console.error(CHATBOT_ERROR_ACCESSING_MEDIA_DEVICES, error);
    }
  }

  const handleDeviceChange = (e) => {
    setSelectedDevice(e.target.value);
  };

  const getDeviceLabel = (device, index) => {
    return device.label || `Micrófono ${index + 1}`;
  };

  const stopRecording = () => {
    if (isCancelRef.current) {
      chunksRef.current = [];
    }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (volumeIntervalRef.current) {
        clearInterval(volumeIntervalRef.current);
      }

      if (audioContextRef.current) {
        scriptProcessorRef.current.disconnect();
        analyserRef.current.disconnect();
        microphoneRef.current.disconnect();
        audioContextRef.current.close();
      }

      // Clear the recording timeout
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
    }
  };

  const handleRecordingStop = () => {
    // Check if we detected any audio activity during recording
    if (!hasAudioActivity) {
      // Alert the user that the audio is empty
      sendMessage({
        from: CHATBOT_CHAT_MSJ_FROM_BOT,
        message: CHATBOT_ERROR_MESSAGE_AUDIO,
      });

      // Reset states
      setIsBlocked(false);
      setVolumeBars([]);
      setAudioData(null);
      return;
    }

    // If we have audio data and activity was detected, proceed with sending it
    if (chunksRef.current.length > 0) {
      const blob = new Blob(chunksRef.current, { type: chatbotUtnMineTypeAudio });
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result.toString().split(',')[1];

        if (!isCancelRef.current) {
          sendMessage({
            from: CHATBOT_CHAT_MSJ_FROM_CLIENT,
            message: '',
            audio: base64data,
          });
        }

        setIsBlocked(false);
        setVolumeBars([]);
        setAudioData(null);
      };
      reader.readAsDataURL(blob);
    }
  };

  const startRecording = async () => {
    if (isLoading) {
      return;
    }
    try {
      // Reset audio activity detection for new recording
      hasAudioActivity = false;
      isCancelRef.current = false;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        audioBitsPerSecond: 128000,
        videoBitsPerSecond: 2500000,
        mimeType: chatbotUtnMineTypeAudio,
      });
      chunksRef.current = [];
      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      mediaRecorder.onstop = handleRecordingStop;
      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);

      // Set a timeout to stop recording after 3 minutes
      recordingTimeoutRef.current = setTimeout(() => {
        stopRecording();
      }, 180000);

      // Setup audio analysis to detect sound
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      microphoneRef.current = audioContextRef.current.createMediaStreamSource(stream);
      scriptProcessorRef.current = audioContextRef.current.createScriptProcessor(256, 1, 1);

      microphoneRef.current.connect(analyserRef.current);
      analyserRef.current.connect(scriptProcessorRef.current);
      scriptProcessorRef.current.connect(audioContextRef.current.destination);

      setVolumeBars([]);

      // Process audio to detect activity
      scriptProcessorRef.current.onaudioprocess = function () {
        const array = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(array);
        const volume = array.reduce((a, b) => a + b) / array.length;

        const normalizedVolume = Math.min(100, Math.max(0.5, (volume / 256) * 50));

        // Check if we have audio activity (volume above threshold)
        if (normalizedVolume > audioActivityThreshold) {
          hasAudioActivity = true;
        }

        // Update volume bars visualization
        setVolumeBars(prevBars => {
          if (prevBars.length < 33) {
            return [...prevBars, normalizedVolume];
          } else {
            return [...prevBars.slice(1), normalizedVolume];
          }
        });
      };

      // Set up interval to check audio activity every 300ms
      const activityCheckInterval = setInterval(() => {
        // Your interval code here if needed
      }, 300);

    } catch (err) {
      console.error(CHATBOT_ERROR_ACCESSING_MEDIA_DEVICES, err);
      sendMessage({
        from: CHATBOT_CHAT_MSJ_FROM_BOT,
        message: CHATBOT_ERROR_ACCESSING_MEDIA_DEVICES,
      });
    }
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    mouseDownExecutedRef.current = false;

    if (textareaRef.current) {
      textareaRef.current.blur();
    }

    if (isBlocked) {
      setIsBlocked(false);
    }

    timerRef.current = setTimeout(() => {
      setRecordOptionsOpened(prevState => !prevState);
      mouseDownExecutedRef.current = true;
    }, 1500);
  };

  const handlePointerUp = (e) => {
    e.preventDefault();
    if (isLoading) {
      return;
    }
    clearTimeout(timerRef.current);
    if (mouseDownExecutedRef.current) {
      mouseDownExecutedRef.current = false;
      return;
    }
    if (isBlocked) {
      return;
    }
    if (isRecording) {
      stopRecording();
      setIsBlocked(true);
    } else {
      startRecording();
    }
  };

  const handleCancelRecording = () => {
    isCancelRef.current = true;
    stopRecording();
    setAudioData(null);
    setIsBlocked(false);
    setVolumeBars([]);
  };

  const handleSendRecording = (e) => {
    e.preventDefault();
    isCancelRef.current = false;
    if (isRecording) {
      stopRecording();
    }
  };

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      if (volumeIntervalRef.current) {
        clearInterval(volumeIntervalRef.current);
      }
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      {isRecording ? (
        <div className="bot-utn-mic-graphic-active">
          <img
            className="bot-utn-rec-audio-icon"
            src={recIcon}
            alt="Botón rojo que marca grabacion de audio"
          />
          <div className="volume-bars-div">
            {volumeBars.map((bar, index) => (
              <div
                key={index}
                className="individual-volume-bars"
                style={{ height: `${bar * 10}%`, transition: 'height 0.1s ease' }}
              ></div>
            ))}
          </div>
          <img
            width={15}
            height={15}
            className="bot-utn-delete-audio-icon bot-utn-mic-active"
            src={iconForDeleteAudio}
            onClick={handleCancelRecording}
            alt="Botón para cancelar grabación"
          />
        </div>
      ) : ''}
      <div className={
        clsx({
          'bot-utn-mic-icon-div-disabled': isLoading,
          'bot-utn-mic-icon-div': !isLoading,
        })}
      >
        <img
          className='bot-utn-mic-icon'
          src={micIcon}
          onClick={getDevices}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          alt="Botón de grabación"
        />
      </div>
      <div style={{ width: 0, height: 0, overflow: 'visible' }}>
        {recordOptionsOpened && (
          <div
            style={{
              width: '150px',
              height: '50px',
              position: 'relative',
              overflow: 'hidden',
              top: '-35px',
              left: '-160px',
            }}
          >
            <select
              disabled={isLoading}
              id="chatbot-utn-audio-input"
              value={selectedDevice}
              ref={selectRef}
              style={{ width: '100%' }}
              onBlur={() => setRecordOptionsOpened(false)}
              onChange={handleDeviceChange}
              className="block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding bg-no-repeat border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none"
            >
              {devices.map((device, index) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {getDeviceLabel(device, index)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {isRecording && (
        <button
          className="bot-utn-send-audio-button"
          type="submit"
          onClick={handleSendRecording}
        >
          <SendButtonImgChatbot isRecording={isRecording} />
        </button>
      )}
    </>
  );
}
